export interface InstallPrompt extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}
export type InstallState = 'idle' | 'prompting' | 'accepted' | 'dismissed' | 'failed' | 'installed';
const dismissalKey = 'wordhuli:web-install-dismissed-until';
const cooldown = 30 * 24 * 60 * 60 * 1000;

/** Browser-owned installation, consumed once and invoked only by a player click. */
export class WebInstall {
  private deferred?: InstallPrompt;
  private dismissedUntil = 0;
  state: InstallState = 'idle';
  engaged = false;
  offline = false;
  installed = false;
  constructor(private changed: () => void = () => {}, private storage?: Pick<Storage, 'getItem' | 'setItem'>,
    private now: () => number = Date.now) {
    try {
      const value = Number(storage?.getItem(dismissalKey));
      if (Number.isFinite(value) && value > 0 && value <= now() + cooldown) this.dismissedUntil = value;
    } catch { /* Installation remains optional if storage is denied. */ }
  }
  get available(): boolean { return Boolean(this.deferred && this.engaged && this.offline && !this.installed && this.state !== 'prompting' && this.state !== 'accepted'); }
  get offer(): boolean { return this.available && this.now() >= this.dismissedUntil; }
  capture(event: InstallPrompt): void {
    if (this.installed || typeof event.prompt !== 'function' || !event.userChoice?.then) return;
    event.preventDefault(); this.deferred = event; this.changed();
  }
  dismiss(): void {
    this.dismissedUntil = this.now() + cooldown;
    try { this.storage?.setItem(dismissalKey, String(this.dismissedUntil)); } catch { /* Session dismissal still applies. */ }
    this.state = 'dismissed'; this.changed();
  }
  markInstalled(): void { this.installed = true; this.deferred = undefined; this.state = 'installed'; this.changed(); }
  async prompt(): Promise<void> {
    if (!this.available) return;
    const event = this.deferred!; this.deferred = undefined;
    this.state = 'prompting'; this.changed();
    try {
      await event.prompt();
      const choice = await event.userChoice;
      if (this.installed) return;
      if (choice.outcome === 'dismissed') this.dismiss();
      else { this.state = 'accepted'; this.changed(); }
    } catch {
      if (!this.installed) { this.dismiss(); this.state = 'failed'; this.changed(); }
    }
  }
}
